import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IsfModel } from '@data-layer/models/isf';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { EntityModel } from '@data-layer/models/entities';
import { FormHelper } from '@helpers/form.helper';
import {
  MarineInsuranceBeneficiaryFromToFormItemEntitiesModel,
  MarineInsuranceBeneficiaryFromToFormItemModel,
  MarineInsuranceQuoteFormModel,
} from '../../models';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { marineInsuranceFromItems, marineInsuranceToItems } from '../../constants';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_EMPTY_RADIO, USA_COUNTRY_CODE } from '@constants/constants';
import { MarineInsuranceFromToFormComponent } from '@shared/marine-insurance-shared/components';

@Component({
  selector: 'app-marine-insurance-from-to-sideform',
  templateUrl: './marine-insurance-sidebar-from-to-form.component.html',
  styleUrls: ['./marine-insurance-sidebar-from-to-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceSidebarFromToFormComponent implements OnInit, OnDestroy {
  @Input() public entity: enumMarineInsuranceEntityType;
  @Input() public marineInsurance: MarineInsuranceModel;
  @Input() public isf: IsfModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public lclQuote: QuoteModel;
  @Input() public lclShipment: LCLShipmentModel;
  @Input() public entityCode: enumEntityModelCode;
  @Input() public isFrom: boolean;
  @Input() public isEdit: boolean;
  @Input() public isSaving: boolean;

  @Output() public save: EventEmitter<MarineInsuranceQuoteFormModel> = new EventEmitter<MarineInsuranceQuoteFormModel>();

  public entityFormGroup: UntypedFormGroup;
  public formGroup: UntypedFormGroup;
  public countryFormControl: UntypedFormControl;
  public stateFormControl: UntypedFormControl;
  public cityFormControl: UntypedFormControl;
  public originEntityCodeFormControl: UntypedFormControl;
  public destinationEntityCodeFormControl: UntypedFormControl;

  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public DEFAULT_EMPTY_RADIO: typeof DEFAULT_EMPTY_RADIO = DEFAULT_EMPTY_RADIO;

  @ViewChild(MarineInsuranceFromToFormComponent)
  private formComponentRef: MarineInsuranceFromToFormComponent;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private fh: FormHelper, private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

  public detectChanges(): void {
    this.cdr.markForCheck();
  }

  public ngOnInit(): void {
    this.entityFormGroup = this.fh.createEntityModelFormGroup(this.entityCode);
    this.formGroup = new UntypedFormGroup({
      addressItemType: new UntypedFormControl(DEFAULT_EMPTY_RADIO),
      originEntityCode: new UntypedFormControl(null),
      originCountry: new UntypedFormControl(null),
      originCity: new UntypedFormControl(null),
      destinationEntityCode: new UntypedFormControl(null),
      destinationCountry: new UntypedFormControl(null),
      destinationState: new UntypedFormControl(null),
      destinationCity: new UntypedFormControl(null),
    });
    this.originEntityCodeFormControl = this.formGroup.get('originEntityCode') as UntypedFormControl;
    this.destinationEntityCodeFormControl = this.formGroup.get('destinationEntityCode') as UntypedFormControl;

    if (this.isFrom) {
      this.countryFormControl = this.formGroup.get('originCountry') as UntypedFormControl;
      this.cityFormControl = this.formGroup.get('originCity') as UntypedFormControl;

      this.countryFormControl.setValidators(Validators.required);
      this.cityFormControl.setValidators(Validators.required);
    } else {
      this.countryFormControl = this.formGroup.get('destinationCountry') as UntypedFormControl;
      this.stateFormControl = this.formGroup.get('destinationState') as UntypedFormControl;
      this.cityFormControl = this.formGroup.get('destinationCity') as UntypedFormControl;

      this.countryFormControl.setValidators(Validators.required);
      this.cityFormControl.setValidators(Validators.required);
    }

    this.formGroup
      .get('addressItemType')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (!val || val === DEFAULT_EMPTY_RADIO) {
          if (!this.isFrom) {
            this.formComponentRef?.resetStateValValidation(); // #9153
            this.stateFormControl.reset();
          } else {
            this.countryFormControl.reset();
          }
          if (this.isFrom) {
            this.originEntityCodeFormControl.setValue(DEFAULT_EMPTY_RADIO);
          } else {
            this.destinationEntityCodeFormControl.setValue(DEFAULT_EMPTY_RADIO);
          }
          this.cityFormControl.reset();
          return;
        }

        const entityDataCode: enumEntityModelCode = this.fromToItems.find(
          (item: MarineInsuranceBeneficiaryFromToFormItemEntitiesModel) => item.fieldToMatch === val,
        )?.entityCode;
        this.entityFormGroup.patchValue(this.getFromToData(val), { emitEvent: false });

        if (this.isFrom) {
          this.countryFormControl.setValue(this.entityFormGroup.get(['address', 'country'])?.value);
          this.cityFormControl.setValue(this.entityFormGroup.get(['address', 'city'])?.value);
          this.originEntityCodeFormControl.setValue(entityDataCode);
        } else {
          this.countryFormControl.setValue(this.entityFormGroup.get(['address', 'country'])?.value);
          this.stateFormControl.setValue(this.entityFormGroup.get(['address', 'state'])?.value);
          this.cityFormControl.setValue(this.entityFormGroup.get(['address', 'city'])?.value);
          this.destinationEntityCodeFormControl.setValue(entityDataCode);
        }
      });

    this.formGroup.patchValue({
      addressItemType:
        this.fromToItems.find(
          (item: MarineInsuranceBeneficiaryFromToFormItemEntitiesModel) =>
            item.entityCode === (this.isFrom ? this.marineInsurance?.originEntityCode : this.marineInsurance?.destinationEntityCode),
        )?.fieldToMatch || DEFAULT_EMPTY_RADIO,
      originEntityCode: this.marineInsurance?.originEntityCode,
      originCountry: this.marineInsurance?.originCountry,
      originCity: this.marineInsurance?.originCity,
      destinationEntityCode: this.marineInsurance?.destinationEntityCode,
      destinationCountry: this.marineInsurance?.destinationCountry,
      destinationState: this.marineInsurance?.destinationState,
      destinationCity: this.marineInsurance?.destinationCity,
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSave(): void {
    this.entityFormGroup.markAllAsTouched();
    this.formGroup.markAllAsTouched();
    // #9153
    this.formComponentRef?.updateInputsValidation();

    if (this.formGroup.valid) {
      this.isSaving = true;

      this.save.emit({
        ...this.formGroup.getRawValue(),
      });
    }
  }

  public radioLabel(label: string): string {
    return `${this.translateService.instant(label)} ${this.translateService.instant('LABELS.ADDRESS_LOWER_CASE')}`;
  }

  public getFromToData(fieldToMatch: string): EntityModel {
    switch (this.entity) {
      case enumMarineInsuranceEntityType.ISF:
        if (fieldToMatch === 'importer') {
          return this.isf?.importer;
        }
        if (fieldToMatch === 'manufacturer') {
          return this.isf?.shipment?.invoices?.[0]?.manufacturer;
        }
        return this.isf?.shipment?.[fieldToMatch];

      case enumMarineInsuranceEntityType.ENTRY_SUMMARY:
        return fieldToMatch === 'importer'
          ? this.entrySummary?.importer
          : fieldToMatch === 'manufacturer'
          ? this.entrySummary?.shipments?.[0]?.invoices?.[0]?.manufacturer
          : this.entrySummary?.shipments?.[0]?.[fieldToMatch];

      case enumMarineInsuranceEntityType.LCL_SHIPPING:
        return this.lclShipment?.[fieldToMatch];

      default:
        return null;
    }
  }

  public get fromToItems(): MarineInsuranceBeneficiaryFromToFormItemEntitiesModel[] {
    return (
      (
        (this.isFrom ? marineInsuranceFromItems : marineInsuranceToItems || []).find(
          (item: MarineInsuranceBeneficiaryFromToFormItemModel) => item.entityType === this.entity,
        )?.entities || []
      ).filter((item: MarineInsuranceBeneficiaryFromToFormItemEntitiesModel) => {
        if (this.entity === enumMarineInsuranceEntityType.LCL_SHIPPING && !this.isFrom && item.fieldToMatch === 'consignee') {
          // #8734
          return this.lclShipment?.consignee?.address?.country?.code === USA_COUNTRY_CODE;
        }

        return !!this.getFromToData(item.fieldToMatch);
      }) || []
    );
  }

  public get fromToRadioLabel(): string {
    return this.isFrom ? 'START_POINT_OF_INSURANCE' : 'END_POINT_OF_INSURANCE';
  }

  public get headerTitle(): string {
    return this.isFrom ? 'MARINE_INSURANCE_FROM_SIDEBAR_TITLE' : 'MARINE_INSURANCE_TO_SIDEBAR_TITLE';
  }

  public get headerHint(): string {
    return this.isFrom ? 'MARINE_INSURANCE_FROM_SIDEBAR_HINT' : 'MARINE_INSURANCE_TO_SIDEBAR_HINT';
  }

  public get isHideState(): boolean {
    return !!this.isFrom;
  }
}
